import React from "react";
import { Award, Target, Eye, RotateCw, Users, Zap } from "lucide-react";

export const PodPhilosophy = () => {
  const podPrinciples = [
    {
      icon: Target,
      title: "Outcome-Driven Excellence",
      description:
        "We measure success by value delivered to clients, not tasks completed. Quality is built in from day one through rigorous processes and continuous improvement.",
      features: [
        "Measured by client outcomes and business impact, not hours logged",
        "3-layer QA process with peer review, QA lead validation, and client readiness",
        "Automated testing integration throughout development cycles",
        "Rigorous standards compliance and proactive risk assessment",
      ],
      color: "from-green-600 to-green-700",
      bgColor: "bg-green-600/10 border border-green-400/20",
    },
    {
      icon: Eye,
      title: "Purpose & Transparency",
      description:
        "We always know the 'Why' behind every decision. All progress, blockers, and outputs are visible with proactive communication and rapid feedback integration.",
      features: [
        "Clear North Star definition for every Pod mission aligned with client goals",
        "Real-time progress visibility through KolaHub dashboard",
        "Daily standups with transparent blocker identification",
        "Proactive client communication and risk escalation",
      ],
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-600/10 border border-green-400/20",
    },
    {
      icon: Zap,
      title: "Agile & Adaptive",
      description:
        "Fast, flexible, and structured around iterative learning with 2-week sprint cycles that respond to changing client needs.",
      features: [
        "2-week sprint cycles with rapid iteration and delivery",
        "Flexible response to changing priorities and client feedback",
        "Continuous deployment and incremental value delivery",
        "Adaptive planning based on real-time insights",
      ],
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-600/10 border border-green-400/20",
    },
    {
      icon: RotateCw,
      title: "Continuous Evolution (Kaizen)",
      description:
        "Every cycle includes systematic reflection, learning, and improvement embedded in our DNA. We obsess over getting better every sprint.",
      features: [
        "Structured retrospectives after every 2-week sprint",
        "Cross-Pod knowledge sharing and best practice exchange",
        "Data-driven process optimization and A/B testing",
        "Monthly playbook updates based on real experience",
      ],
      color: "from-orange-500 to-red-500",
      bgColor: "bg-orange-600/10 border border-orange-400/20",
    },
    {
      icon: Users,
      title: "Human-Centered Cohesion",
      description:
        "Designed around people and processes. We work as one unified team, seamlessly integrated with client workflows and team well-being at the foundation.",
      features: [
        "Cross-functional team design with built-in redundancy",
        "Seamless integration with client systems and processes",
        "Collaborative development with pair programming",
        "Strong team health monitoring and support systems",
      ],
      color: "from-green-600 to-green-700",
      bgColor: "bg-green-600/10 border border-green-400/20",
    },
    {
      icon: Award,
      title: "End-to-End Ownership",
      description:
        "Each Pod owns the full lifecycle—from ideation to delivery and beyond. We're not vendors; we're strategic partners who own outcomes.",
      features: [
        "Complete accountability from planning through post-deployment",
        "Disciplined project management with clear timelines",
        "Proactive risk mitigation and issue resolution",
        "Long-term partnership mindset beyond single projects",
      ],
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-600/10 border border-green-400/20",
    },
  ];

  return (
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Calvin's Quote */}
        <div className="mb-16 text-center">
          <blockquote className="bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-3xl p-8 text-white max-w-4xl mx-auto">
            <p className="text-2xl font-medium mb-4">
              "Become a Mover, Don't Be Moved"
            </p>
            <cite className="text-brand-secondary font-semibold">
              — Calvin Magezi, CTO
            </cite>
          </blockquote>
        </div>

        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-brand-grey mb-6">
            Our Core <span className="text-brand-primary">Pod Philosophy</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Six fundamental principles that guide every Pod's operation,
            ensuring consistent excellence and predictable outcomes for our
            clients.
          </p>
        </div>

        <div className="space-y-12">
          {podPrinciples.map((principle, index) => (
            <div
              key={index}
              className={`group relative overflow-hidden rounded-3xl transition-all duration-500 hover:-translate-y-2 ${
                index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
              }`}
            >
              <div className="bg-white border border-gray-200 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  {/* Content Side */}
                  <div
                    className={`p-8 lg:p-12 ${index % 2 === 1 ? "lg:order-2" : ""}`}
                  >
                    <div
                      className={`w-16 h-16 ${principle.bgColor} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <principle.icon
                        className={`w-8 h-8 bg-gradient-to-r ${principle.color} bg-clip-text`}
                        style={{ stroke: `url(#gradient-${index})` }}
                      />
                      <svg width="0" height="0">
                        <defs>
                          <linearGradient
                            id={`gradient-${index}`}
                            x1="0%"
                            y1="0%"
                            x2="100%"
                            y2="100%"
                          >
                            <stop
                              offset="0%"
                              stopColor={
                                principle.color.includes("orange")
                                  ? "#f59e0b"
                                  : "#22c55e"
                              }
                            />
                            <stop
                              offset="100%"
                              stopColor={
                                principle.color.includes("orange")
                                  ? "#ef4444"
                                  : "#16a34a"
                              }
                            />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>

                    <h3
                      className={`text-2xl font-bold mb-4 bg-gradient-to-r ${principle.color} bg-clip-text text-transparent`}
                    >
                      {principle.title}
                    </h3>

                    <p className="text-gray-600 leading-relaxed mb-6">
                      {principle.description}
                    </p>

                    <div className="space-y-3">
                      {principle.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start">
                          <div
                            className={`w-2 h-2 rounded-full bg-gradient-to-r ${principle.color} mt-2 mr-3 flex-shrink-0`}
                          ></div>
                          <span className="text-gray-600 text-sm">
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Visual Side */}
                  <div
                    className={`relative ${principle.bgColor} flex items-center justify-center p-8 lg:p-12 ${index % 2 === 1 ? "lg:order-1" : ""}`}
                  >
                    <div className="relative">
                      {/* Large Icon */}
                      <div
                        className={`w-32 h-32 bg-gradient-to-r ${principle.color} rounded-3xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-500`}
                      >
                        <principle.icon className="w-16 h-16 text-white" />
                      </div>

                      {/* Floating Elements */}
                      <div
                        className={`absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r ${principle.color} rounded-full opacity-60 animate-pulse`}
                      ></div>
                      <div
                        className={`absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-r ${principle.color} rounded-full opacity-40 animate-pulse`}
                        style={{ animationDelay: "1s" }}
                      ></div>
                      <div
                        className={`absolute top-1/2 -left-8 w-4 h-4 bg-gradient-to-r ${principle.color} rounded-full opacity-30 animate-pulse`}
                        style={{ animationDelay: "2s" }}
                      ></div>
                    </div>

                    {/* Number Badge */}
                    <div
                      className={`absolute top-4 right-4 w-12 h-12 bg-gradient-to-r ${principle.color} rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg`}
                    >
                      {index + 1}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA with Metrics */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-3xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Experience Philosophy in Action
            </h3>
            <p className="text-brand-secondary mb-6 max-w-2xl mx-auto">
              See how our six core principles translate into measurable results:
              higher quality, faster delivery, and better client satisfaction.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <div className="text-2xl font-bold text-white">≥4.5/5</div>
                <div className="text-brand-secondary text-sm">
                  Client Satisfaction
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">99.9%</div>
                <div className="text-brand-secondary text-sm">
                  Delivery Reliability
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">2 weeks</div>
                <div className="text-brand-secondary text-sm">
                  Sprint Cycles
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">24/7</div>
                <div className="text-brand-secondary text-sm">Transparency</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
